import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';

const SalesTargetForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    targetName: '',
    description: '',
    targetLevel: 'Individual',
    targetEntityId: '',
    targetEntityValue: '',
    periodType: 'Quarterly',
    periodYear: new Date().getFullYear(),
    periodQuarter: 1,
    periodMonth: 1,
    startDate: '',
    endDate: '',
    status: 'Active'
  });

  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchTarget();
    } else {
      calculateDates();
    }
  }, [id]);

  useEffect(() => {
    if (!isEdit) {
      calculateDates();
    }
  }, [formData.periodType, formData.periodYear, formData.periodQuarter, formData.periodMonth]);

  const fetchTarget = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/salestargets/${id}`);
      const target = response.data.target;

      setFormData({
        targetName: target.TargetName,
        description: target.Description || '',
        targetLevel: target.TargetLevel,
        targetEntityId: target.TargetEntityID || '',
        targetEntityValue: target.TargetEntityValue || '',
        periodType: target.PeriodType,
        periodYear: target.PeriodYear,
        periodQuarter: target.PeriodQuarter || 1,
        periodMonth: target.PeriodMonth || 1,
        startDate: target.StartDate?.split('T')[0] || '',
        endDate: target.EndDate?.split('T')[0] || '',
        status: target.Status
      });

      setMetrics(target.Metrics || []);
    } catch (error) {
      console.error('Error fetching target:', error);
      alert('Failed to load target');
    } finally {
      setLoading(false);
    }
  };

  const calculateDates = async () => {
    try {
      const response = await axios.post('/api/salestargets/calculate-period-dates', {
        periodType: formData.periodType,
        year: formData.periodYear,
        quarter: formData.periodQuarter,
        month: formData.periodMonth
      });

      setFormData(prev => ({
        ...prev,
        startDate: response.data.dates.startDate,
        endDate: response.data.dates.endDate
      }));
    } catch (error) {
      console.error('Error calculating dates:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleAddMetric = () => {
    setMetrics(prev => [...prev, {
      MetricID: null,
      MetricType: 'Revenue',
      MetricName: '',
      TargetValue: 0,
      Unit: 'THB',
      Weight: 1
    }]);
  };

  const handleUpdateMetric = (index, field, value) => {
    setMetrics(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRemoveMetric = (index) => {
    setMetrics(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.targetName.trim()) {
      newErrors.targetName = 'Target name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (formData.targetLevel === 'Individual' && !formData.targetEntityId) {
      newErrors.targetEntityId = 'User selection is required for individual targets';
    }

    if (formData.targetLevel === 'Team' && !formData.targetEntityValue.trim()) {
      newErrors.targetEntityValue = 'Team/department name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setSaving(true);

      const data = {
        targetName: formData.targetName,
        description: formData.description,
        targetLevel: formData.targetLevel,
        targetEntityId: formData.targetEntityId || null,
        targetEntityValue: formData.targetEntityValue || null,
        periodType: formData.periodType,
        periodYear: formData.periodYear,
        periodQuarter: formData.periodType === 'Quarterly' ? formData.periodQuarter : null,
        periodMonth: formData.periodType === 'Monthly' ? formData.periodMonth : null,
        startDate: formData.startDate,
        endDate: formData.endDate,
        status: formData.status
      };

      let targetId = id;

      if (isEdit) {
        await axios.put(`/api/salestargets/${id}`, data);
      } else {
        const response = await axios.post('/api/salestargets', data);
        targetId = response.data.target.TargetID;
      }

      // Save metrics
      for (const metric of metrics) {
        await axios.post(`/api/salestargets/${targetId}/metrics`, {
          metricId: metric.MetricID,
          metricType: metric.MetricType,
          metricName: metric.MetricName,
          targetValue: metric.TargetValue,
          unit: metric.Unit,
          weight: metric.Weight
        });
      }

      alert(isEdit ? 'Target updated successfully' : 'Target created successfully');
      navigate(`/salestargets/${targetId}`);
    } catch (error) {
      console.error('Error saving target:', error);
      alert(error.response?.data?.message || 'Failed to save target');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/salestargets')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Sales Target' : 'New Sales Target'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Name *
              </label>
              <input
                type="text"
                value={formData.targetName}
                onChange={(e) => handleChange('targetName', e.target.value)}
                className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.targetName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Q1 2026 Revenue Target"
              />
              {errors.targetName && (
                <p className="mt-1 text-xs text-red-600">{errors.targetName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Target Assignment */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Assignment</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Level *
              </label>
              <select
                value={formData.targetLevel}
                onChange={(e) => handleChange('targetLevel', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Individual">Individual</option>
                <option value="Team">Team/Department</option>
                <option value="Company">Company-wide</option>
              </select>
            </div>

            {formData.targetLevel === 'Individual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to User *
                </label>
                <input
                  type="number"
                  value={formData.targetEntityId}
                  onChange={(e) => handleChange('targetEntityId', e.target.value)}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.targetEntityId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="User ID"
                />
                {errors.targetEntityId && (
                  <p className="mt-1 text-xs text-red-600">{errors.targetEntityId}</p>
                )}
              </div>
            )}

            {formData.targetLevel === 'Team' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team/Department Name *
                </label>
                <input
                  type="text"
                  value={formData.targetEntityValue}
                  onChange={(e) => handleChange('targetEntityValue', e.target.value)}
                  className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.targetEntityValue ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Sales Department"
                />
                {errors.targetEntityValue && (
                  <p className="mt-1 text-xs text-red-600">{errors.targetEntityValue}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Time Period */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Period</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Period Type *
              </label>
              <select
                value={formData.periodType}
                onChange={(e) => handleChange('periodType', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year *
              </label>
              <select
                value={formData.periodYear}
                onChange={(e) => handleChange('periodYear', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {formData.periodType === 'Quarterly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quarter *
                </label>
                <select
                  value={formData.periodQuarter}
                  onChange={(e) => handleChange('periodQuarter', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={1}>Q1 (Jan-Mar)</option>
                  <option value={2}>Q2 (Apr-Jun)</option>
                  <option value={3}>Q3 (Jul-Sep)</option>
                  <option value={4}>Q4 (Oct-Dec)</option>
                </select>
              </div>
            )}

            {formData.periodType === 'Monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month *
                </label>
                <select
                  value={formData.periodMonth}
                  onChange={(e) => handleChange('periodMonth', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {[
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                  ].map((month, idx) => (
                    <option key={idx + 1} value={idx + 1}>{month}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange('startDate', e.target.value)}
                className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.startDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.startDate && (
                <p className="mt-1 text-xs text-red-600">{errors.startDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date *
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange('endDate', e.target.value)}
                className={`w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.endDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.endDate && (
                <p className="mt-1 text-xs text-red-600">{errors.endDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Metrics</h3>
            <button
              type="button"
              onClick={handleAddMetric}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Metric
            </button>
          </div>

          {metrics.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No metrics added yet</p>
              <p className="text-xs mt-1">Add metrics to track progress</p>
            </div>
          ) : (
            <div className="space-y-4">
              {metrics.map((metric, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={metric.MetricType}
                        onChange={(e) => handleUpdateMetric(index, 'MetricType', e.target.value)}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                      >
                        <option value="Revenue">Revenue</option>
                        <option value="NewCustomers">New Customers</option>
                        <option value="DealsWon">Deals Won</option>
                        <option value="Activities">Activities</option>
                        <option value="Custom">Custom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={metric.MetricName}
                        onChange={(e) => handleUpdateMetric(index, 'MetricName', e.target.value)}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Target Value
                      </label>
                      <input
                        type="number"
                        value={metric.TargetValue}
                        onChange={(e) => handleUpdateMetric(index, 'TargetValue', parseFloat(e.target.value))}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={metric.Unit}
                        onChange={(e) => handleUpdateMetric(index, 'Unit', e.target.value)}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                        placeholder="THB, Count, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Weight
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={metric.Weight}
                        onChange={(e) => handleUpdateMetric(index, 'Weight', parseFloat(e.target.value))}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => handleRemoveMetric(index)}
                        className="w-full inline-flex items-center justify-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/salestargets')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : isEdit ? 'Update Target' : 'Create Target'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SalesTargetForm;


